import React, { type ComponentProps, type RefObject, useCallback, useEffect, useRef } from 'react';
import {
  type LayoutChangeEvent,
  type NativeMethods,
  type ScrollView,
  StyleSheet,
} from 'react-native';

import { theme } from '@garnish/constants';

import { Button } from '../../../Button';

// ─────────────────────────────────────────────────────────────────────────────

export const ScrollspyV2NavItem = (props: ScrollspyV2NavItemProps) => {
  const {
    children,
    targetId,
    isActive = false,
    navScrollViewRef,
    onPress,
    onLayout,
    ...buttonProps
  } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────

  const ref = useRef(null);
  const layoutRef = useRef({ x: 0, width: 0 });

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress?.(targetId);
  }, [onPress, targetId]);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { x, width } = event.nativeEvent.layout;

      layoutRef.current = { x, width };

      if (typeof onLayout === 'function') {
        onLayout(event);
      }
    },
    [onLayout],
  );

  const improveScrollPositionIfNeeded = useCallback(() => {
    if (!isActive || !navScrollViewRef?.current) return;

    const scrollView = navScrollViewRef.current;
    const itemNode = ref.current as NativeMethods | null;

    if (!itemNode) return;

    // Compare absolute screen positions to check visibility cross-platform.
    itemNode.measure((_ix, _iy, itemWidth, _ih, itemPageX) => {
      (scrollView as unknown as NativeMethods).measure((_nx, _ny, navWidth, _nh, navPageX) => {
        const viewportX = itemPageX - navPageX;
        const isComfortablyVisible =
          viewportX >= EDGE_PADDING && viewportX + itemWidth <= navWidth - EDGE_PADDING;

        if (isComfortablyVisible) return;

        const { x: contentX } = layoutRef.current;

        scrollView.scrollTo({ x: Math.max(0, contentX - EDGE_PADDING), animated: true });
      });
    });
  }, [navScrollViewRef, isActive]);

  // ─── Effects ───────────────────────────────────────────────────────────

  useEffect(improveScrollPositionIfNeeded, [improveScrollPositionIfNeeded]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Button
      ref={ref}
      size="large"
      palette={isActive ? 'primary' : 'secondary'}
      style={styles.pressable}
      accessibilityState={{ selected: isActive }}
      onPress={handleOnPress}
      onLayout={handleLayout}
      {...buttonProps}
    >
      {children}
    </Button>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const EDGE_PADDING = 48;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressable: {
    height: 32,
    paddingHorizontal: theme.spacing['3'],
    borderWidth: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScrollspyV2NavItemProps = {
  targetId: string;
  isActive?: boolean;
  navScrollViewRef?: RefObject<ScrollView | null>;
  onPress?: (targetId: string) => void;
} & Omit<ComponentProps<typeof Button>, 'palette' | 'onPress'>;
